import React, { useState, useEffect } from "react";
import ReactQuill from "react-quill";
import { useSendBulkEmailMutation } from "../store/apiSlice"; // Import mutation
import "react-quill/dist/quill.snow.css"; // Default Quill toolbar styling
import { FixedSizeList as List } from 'react-window';
import { useSelector } from 'react-redux';

const predefinedEmails = [
  "gabrielle@teachingjobs.com.au",
  "jarrod@teachingjobs.com.au"
]; // Example predefined emails

const Row = ({ index, style, data, onRemove }) => {
  const recipient = data[index];

  return (
    <div
      style={style}
      className="px-2 py-1 bg-gray-200 flex items-center gap-2 mb-2 mr-2"
    >
      {recipient.firstName} ({recipient.email})
      <button
        onClick={() => onRemove(recipient.email)}
        className="text-red-500 hover:text-red-700"
      >
        ✕
      </button>
    </div>
  );
};


const EmailEditor = ({ jobData, contacts, onClose }) => {
  const userInfo = useSelector((state) => state.auth.userInfo); // Get userInfo from state
  const [emailBody, setEmailBody] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [recipients, setRecipients] = useState(contacts); // Store full recipient objects
  const [newRecipientEmail, setNewRecipientEmail] = useState("");
  const [newRecipientName, setNewRecipientName] = useState("");
  const [sourceEmail, setSourceEmail] = useState(predefinedEmails[0]); // Default to first email
  const [sendBulkEmail, { isLoading, isError, error }] = useSendBulkEmailMutation();

  useEffect(() => {
    if (jobData?.academic_connections?.length) {
      const connectionList = jobData.academic_connections
        .map(
          (conn, i) =>
            `${i + 1}. ${conn.firstname.charAt(0)}. ${conn.lastname} - ${conn.jobTitle ? ` (${conn.jobTitle} - ${conn.note})` : ""}`
        )
        .join("<br/>");

      const bodyTemplate = `
        <p>Your <strong>${jobData.title}</strong> has just reached these top academics in your job's field of expertise.
        Our A.I. explored and matched people in. If you wish us to expand our reach to neighboring states, then please let me know.</p>
        <p><strong>Academic Connections:</strong><br/>${connectionList}</p>
        <p>Regards,<br/>Sarah West,</br>Academic Jobs</p>
      `;

      setEmailSubject(`Academic Leads for ${jobData.title}`);
      setEmailBody(bodyTemplate);
    }
  }, [jobData]);

  if (userInfo?.userType !== "Manager") {
    return <p>You do not have permission to access this editor.</p>;
  }

  // Remove recipient from the list
  const removeRecipient = (email) => {
    setRecipients(recipients.filter((recipient) => recipient.email !== email));
  };

  // Add recipient to the list
  const addRecipient = () => {
    if (!newRecipientEmail.trim() || recipients.some((rec) => rec.email === newRecipientEmail.trim())) {
      return;
    }

    const newRecipient = {
      email: newRecipientEmail.trim(),
      firstName: newRecipientName.trim() || "Valued Customer", // Default if no name provided
    };

    setRecipients([...recipients, newRecipient]);
    setNewRecipientEmail("");
    setNewRecipientName(""); // Reset fields
  };

  const chunkArray = (arr, chunkSize) => {
    const chunks = [];
    for (let i = 0; i < arr.length; i += chunkSize) {
      chunks.push(arr.slice(i, i + chunkSize));
    }
    return chunks;
  };

  const handleSendEmail = async () => {
    console.log("📧 Starting email sending process...");

    if (!emailSubject.trim() || !emailBody.trim()) {
      alert("Please enter a subject and email body before sending.");
      console.log("❌ Email subject or body is missing.");
      return;
    }

    const batchSize = 20;
    const recipientChunks = chunkArray(recipients, batchSize);

    console.log(`📋 Total recipients: ${recipients.length}`);
    console.log(`📦 Divided into ${recipientChunks.length} chunks of up to ${batchSize} recipients each.`);

    try {
      for (const [index, chunk] of recipientChunks.entries()) {
        console.log(`🚀 Sending batch ${index + 1} with ${chunk.length} recipients...`);
        console.log("📧 Recipients in this batch:", chunk);

        await sendBulkEmail({
          recipients: chunk,
          subject: emailSubject,
          body: emailBody,
          source: sourceEmail,
        }).unwrap();

        console.log(`✅ Batch ${index + 1} sent successfully.`);
      }

      alert("✅ All emails sent successfully!");
      console.log("🎉 Email sending process completed successfully.");
      onClose();
    } catch (err) {
      console.error("❌ Failed to send email batch:", err);
      alert("❌ Some emails may have failed. Please try again.");
    }
  };


  return (
    <div className="p-4 mb-4 border border-gray-300 rounded bg-gray-100">
      <h2 className="text-lg font-bold mb-2">Compose Email</h2>

      {/* Source Email Selection */}
      <div className="mb-4">
        <label className="block font-semibold mb-1">Send From:</label>
        <select
          value={sourceEmail}
          onChange={(e) => setSourceEmail(e.target.value)}
          className="p-2 border border-gray-300 rounded w-full"
        >
          {predefinedEmails.map((email) => (
            <option key={email} value={email}>
              {email}
            </option>
          ))}
        </select>
      </div>

      {/* Recipients List */}
      <List
        height={300}
        itemCount={recipients.length}
        itemSize={40}
        width="100%"
        itemData={recipients}
        className="my-4 p-2"
      >
        {({ index, style, data }) => (
          <Row
            index={index}
            style={style}
            data={data}
            onRemove={removeRecipient}
          />
        )}
      </List>

      {/* Add Recipient Field */}
      <label className="block font-semibold mb-1">Add another contact</label>
      <div className="flex items-center gap-2 mb-4">
        <input
          type="text"
          placeholder="First Name (optional)"
          value={newRecipientName}
          onChange={(e) => setNewRecipientName(e.target.value)}
          className="p-2 border border-gray-300 rounded flex-grow"
        />
        <input
          type="email"
          placeholder="Recipient Email"
          value={newRecipientEmail}
          onChange={(e) => setNewRecipientEmail(e.target.value)}
          className="p-2 border border-gray-300 rounded flex-grow"
        />
        <button onClick={addRecipient} className="px-3 py-2 bg-green-500 text-white rounded">
          Add
        </button>
      </div>

      {/* Subject Input Field */}
      <label className="block font-semibold mb-1">Email Subject</label>
      <input
        type="text"
        placeholder="Enter email subject..."
        value={emailSubject}
        onChange={(e) => setEmailSubject(e.target.value)}
        className="w-full p-2 border border-gray-300 rounded mb-4"
      />

      {/* React Quill Editor */}
      <label className="block font-semibold mb-1">NOTE: maximum of 5 images in email body.</label>
      <ReactQuill
        value={emailBody}
        onChange={setEmailBody}
        className="mb-4 bg-white"
        theme="snow"
        modules={{
          toolbar: [
            [{ header: [1, 2, false] }],
            ["bold", "italic", "underline", "strike"],
            [{ list: "ordered" }, { list: "bullet" }],
            ["link"],
            ["clean"],
          ],
        }}
        style={{ minHeight: "150px" }}
      />

      {/* Error Message */}
      {isError && (
        <p className="text-red-500 mt-2">❌ Failed to send email. {error?.data?.message || "Please try again."}</p>
      )}

      {/* Buttons */}
      <div className="flex justify-between mt-4">
        <button onClick={onClose} className="px-4 py-2 bg-gray-400 text-white rounded">
          Cancel
        </button>
        <button
          onClick={handleSendEmail}
          disabled={isLoading}
          className={`px-4 py-2 rounded ${isLoading ? "bg-gray-500 cursor-not-allowed" : "bg-blue-500 text-white"}`}
        >
          {isLoading ? "Sending..." : "Send Email"}
        </button>
      </div>
    </div>
  );
};

export default EmailEditor;
